<template>
	<Head :title="`${title} | ${appName}`"/>
	<section class="relative flex w-screen max-w-screen">
		<aside id="aareii-menu" class="fixed top-0 left-0 right-0 hidden h-dvh w-screen z-50 md:relative md:w-80 md:py-2 md:pl-2 md:flex md:z-auto overflow-y-auto">
			<nav class="p-2 w-full md:fixed md:w-76 md:h-[calc(100dvh-1rem)] flex flex-col bg-linear-to-br from-white to-gray-50 dark:from-gray-900 dark:to-gray-950 shadow-md dark:shadow-black md:rounded-lg text-gray-800 dark:text-gray-100 divide-y divide-gray-200 dark:divide-gray-800 overflow-y-auto">
				<div class="px-1 pb-2 md:hidden flex items-center justify-between gap-3">
					<SecondaryButton :color="Color.Gray" @click.stop="toggleMenu">
						<i class="fa-solid fa-xmark"/>
					</SecondaryButton>
					<div class="grow"/>
					<Link href="/">
						<img :src="storage('logos/SVG/white/letters.svg')" class="hidden dark:inline-block h-8.5" alt="AArEII Logo">
						<img :src="storage('logos/SVG/letters.svg')" class="dark:hidden h-8.5" alt="AArEII Logo">
					</Link>
				</div>
				<div id="menu-content" class="py-2 md:pt-0 grow flex flex-col items-stretch gap-1 overflow-y-auto">
					<template v-if="isLoggedIn">
						<template v-for="item in menuItems">
							<MenuItemComponent :item="item"/>
						</template>
					</template>
				</div>
				<div id="menu-footer" class="pt-2 flex flex-col gap-1">
					<template v-if="isLoggedIn">
						<a v-if="prefersEnglish" :href="route('locale.switch', 'es')">Cambiar a español (AR)</a>
						<a v-else :href="route('locale.switch', 'en')">Switch to English (US)</a>
						<Link :href="route('logout')" method="post" data-testid="log-out-button" class="text-red-400">{{ trans('Logout') }}</Link>
					</template>
					<Link v-else :href="route('login')" class="text-green-400">{{ trans('Login') }}</Link>
					<div class="flex justify-between items-center gap-2">
						<Link :href="route('version.index')" class="text-pink-400">{{ appVersion }}</Link>
						<ThemeChooser/>
					</div>
				</div>
			</nav>
		</aside>
		<main class="relative grow">
			<ActionableHeader
				:title="title"
				:subtitle="subtitle"
				:return-url="returnUrl"
				:return-label="returnLabel"
				@toggle-menu="toggleMenu"
			>
				<slot name="actions"/>
			</ActionableHeader>
			<section class="mx-auto w-full md:w-lg lg:w-3xl xl:w-5xl 2xl:w-6xl p-4 flex flex-col gap-3">
				<slot/>
			</section>
		</main>
	</section>
</template>
<script setup lang="ts">
import {route} from 'ziggy-js'
import {storage} from '@/helpers'
import {trans} from 'laravel-vue-i18n'
import {usePage} from '@inertiajs/vue3'
import {Color} from '@/Types/Enums/Color'
import {Link, Head} from '@inertiajs/vue3'
import ThemeChooser from '@/Components/ThemeChooser.vue'
import MenuItemComponent from '@/Components/Menu/MenuItem.vue'
import {type MenuItem} from '@/Types/Components/Menu/MenuItem'
import ActionableHeader from '@/Components/ActionableHeader.vue'
import SecondaryButton from '@/Components/Buttons/SecondaryButton.vue'
import {defineProps, ref, type Ref, computed, type ComputedRef} from 'vue'

defineProps<{
	title:string,
	subtitle?:string,
	returnUrl?:string,
	returnLabel?:string,
}>()

const page = usePage()

const appName:ComputedRef<string> = computed(():string => page.props.appName as string)
const appVersion:ComputedRef<string> = computed(():string => page.props.appVersion as string)
const isLoggedIn:ComputedRef<boolean> = computed(():boolean => page.props.isLoggedIn as boolean)
const prefersEnglish:ComputedRef<boolean> = computed(():boolean => page.props.prefersEnglish as boolean)

const canViewAssemblies:ComputedRef<boolean> = computed(():boolean => page.props.canViewAssemblies as boolean)
const canCreateEventRequests:ComputedRef<boolean> = computed(():boolean => page.props.canCreateEventRequests as boolean)
const canRequestRepresentativeRotation:ComputedRef<boolean> = computed(():boolean => page.props.canRequestRepresentativeRotation as boolean)
const canCreateCollegeZoneRepresentatives:ComputedRef<boolean> = computed(():boolean => page.props.canCreateCollegeZoneRepresentatives as boolean)

const menuItems:Ref<Array<MenuItem>> = ref([
	{
		id: 'home',
		icon: 'fa-solid fa-home',
		title: trans('menu.home'),
		link: route('home'),
		visible: true,
	},
	{
		id: 'account.',
		icon: 'fa-solid fa-user',
		title: trans('menu.account'),
		link: route('account.edit'),
		visible: true,
	},
	{
		id: 'events.',
		icon: 'fa-solid fa-calendar-day',
		title: trans('menu.events.title'),
		visible: true,
		children: [
			{
				id: 'events.index',
				icon: 'fa-solid fa-list-check',
				title: trans('menu.events.register'),
				link: route('events.index'),
				visible: true,
			},
			{
				id: 'events.enrollments.',
				icon: 'fa-solid fa-ticket',
				title: trans('menu.events.enrollments'),
				link: route('events.enrollments.index'),
				visible: true,
			},
			{
				id: 'events.certificates.',
				icon: 'fa-solid fa-award',
				title: trans('menu.events.certificates'),
				link: route('events.certificates.index'),
				visible: true,
			},
			{
				id: 'events.requests.',
				icon: 'fa-solid fa-clipboard-list',
				title: trans('menu.events.requests'),
				link: route('events.requests.index'),
				visible: canCreateEventRequests.value,
			},
		],
	},
	{
		id: 'association.',
		icon: 'fa-solid fa-sitemap',
		title: trans('menu.association.title'),
		visible: true,
		children: [
			{
				id: 'association.assemblies.',
				icon: 'fa-solid fa-users-between-lines',
				title: trans('menu.association.assemblies'),
				link: route('association.assemblies.index'),
				visible: canViewAssemblies.value,
			},
			{
				id: 'association.college-zone-representatives.',
				icon: 'fa-solid fa-users-rays',
				title: trans('menu.association.college-zone-representatives'),
				link: route('association.college-zone-representatives.index'),
				visible: canCreateCollegeZoneRepresentatives.value,
			},
			{
				id: 'association.academic-certificates.',
				icon: 'fa-solid fa-file-circle-check',
				title: trans('menu.association.academic-certificates'),
				link: route('association.academic-certificates.index'),
				visible: true,
			},
			{
				id: 'association.representative-rotations.',
				icon: 'fa-solid fa-people-arrows',
				title: trans('menu.association.representative-rotations.title'),
				visible: true,
				children: [
					{
						id: 'association.representative-rotations.index',
						icon: 'fa-solid fa-check-to-slot',
						title: trans('menu.association.representative-rotations.register'),
						link: route('association.representative-rotations.index'),
						visible: true,
					},
					{
						id: 'association.representative-rotations.requests.create',
						icon: 'fa-solid fa-calendar-plus',
						title: trans('menu.association.representative-rotations.requests'),
						link: route('association.representative-rotations.requests.create'),
						visible: canRequestRepresentativeRotation.value,
					},
					{
						id: 'association.representative-rotations.history.',
						icon: 'fa-solid fa-history',
						title: trans('menu.association.representative-rotations.history'),
						link: route('association.representative-rotations.history.index'),
						visible: true,
					},
				],
			},
		],
	},
])

function toggleMenu():void{
	const menu:HTMLElement|null = document.getElementById('aareii-menu')
	if(menu){
		if(menu.classList.contains('flex')){
			menu.classList.add('hidden')
			menu.classList.remove('flex')
		} else {
			menu.classList.add('flex')
			menu.classList.remove('hidden')
		}
	}
}
</script>
